/**
 * T6002 · SSR-safe first-paint gate for NATS connect.
 *
 * SPEC ANCHOR: `specs/009-data-fetching-audit/05-new-infra-sdk-changes.html`
 * §SSR-safe-acquire — a NATS event arriving between dehydrate (server) and
 * hydrate (client commit) can trigger `setQueryData` BEFORE React commits
 * the hydrated state. Verified failure class in TanStack/query #9642.
 *
 * THE GATE
 * --------
 * `natsClient.connect()` is gated on first-paint-complete:
 *   - web: one-shot `window.requestIdleCallback` listener fired AFTER the
 *     first `useEffect` runs. If `requestIdleCallback` is unavailable
 *     (Safari < 17), a `setTimeout(0)` fallback covers the same commit-
 *     drain semantics.
 *   - RN:  the first JS frame after `SdkScreen` mount (set by the platform
 *     adapter via `markFirstPaintComplete()`).
 *
 * The WS is not opened until the gate releases — so no events arrive on
 * the wire in the meantime. The CONTRACT this module guarantees is:
 *
 *   AFTER `markFirstPaintComplete()` has been called once, every
 *   subsequent `whenFirstPaintComplete()` resolves immediately, and every
 *   prior call also resolves.
 *
 * NO 256-EVENT RING BUFFER
 * ------------------------
 * An earlier draft of the SSR-acquire path included a 256-event pre-init
 * ring buffer. With `connect` gated, the wire is silent before hydrate —
 * the buffer is dead code. It was removed to avoid implying overflow
 * semantics that don't exist on this path. NOTE: the RN AppState 'inactive'
 * 256-entry bounded ring (T7135, spec §2971) is a SEPARATE, live deliverable
 * — different lifecycle, different wire state — and is unaffected.
 */

let gateResolved = false;
const waiters: Array<() => void> = [];

/**
 * Resolves on first-paint-complete. Idempotent: subsequent calls after
 * `markFirstPaintComplete()` has fired resolve immediately.
 *
 * Returns a Promise<void>. Callers should `await` this BEFORE opening any
 * long-lived transport that could trigger `setQueryData`.
 */
export function whenFirstPaintComplete(): Promise<void> {
  if (gateResolved) return Promise.resolve();
  return new Promise<void>((resolve) => {
    waiters.push(resolve);
  });
}

/**
 * Mark first-paint as complete. Called ONCE from `useRealtimeQuery`'s
 * first `useEffect` on web, or from the platform adapter's first-frame
 * callback on RN.
 *
 * The actual gate release is deferred one more tick via
 * `requestIdleCallback` (or `setTimeout(0)` fallback) so the React commit
 * phase fully drains BEFORE any waiter's `.then()` runs. Without this
 * defer, a synchronous `setQueryData` in a waiter could land mid-commit
 * and trigger the same hydration mismatch the gate is meant to prevent.
 */
export function markFirstPaintComplete(): void {
  if (gateResolved) return;

  const ric =
    typeof globalThis !== 'undefined' &&
    typeof (globalThis as { requestIdleCallback?: unknown }).requestIdleCallback === 'function';

  const arm = ric
    ? (cb: () => void): void => {
        (
          globalThis as {
            requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void;
          }
        ).requestIdleCallback(cb, { timeout: 50 });
      }
    : (cb: () => void): void => {
        setTimeout(cb, 0);
      };

  arm(() => {
    if (gateResolved) return;
    gateResolved = true;
    const drained = waiters.splice(0);
    for (const fn of drained) fn();
  });
}

/** Test seam — resets the gate so each test starts with a fresh latch. */
export function _resetFirstPaintGateForTest(): void {
  gateResolved = false;
  waiters.splice(0);
}

/**
 * Test seam — read-only inspection of the gate state. Useful for tests
 * that need to assert the gate has NOT been resolved yet.
 */
export function _isGateResolvedForTest(): boolean {
  return gateResolved;
}
